import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { getGuide, GuideIndex, listGuides } from '../api/guides';
import { queryKeys } from './keys';

/**
 * Guides change only when the owning service deploys, never while someone is
 * reading one; the same generous staleTime as the error catalogue keeps a
 * reader clicking between guides from re-fetching the index each time.
 */
const GUIDES_STALE_TIME = 5 * 60 * 1_000;

const noGuides = (apiId: string): GuideIndex => ({
  apiId,
  displayName: '',
  updatedAt: '',
  guides: [],
});

/**
 * A service's guide index. A 404 — no bundle published — is the ordinary case
 * for most services and resolves to an empty index, so the API docs page can
 * simply not render the section rather than treat the absence as an error.
 */
export function useGuides(apiId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.guides(apiId ?? ''),
    queryFn: () =>
      listGuides(apiId!).catch((err: unknown) => {
        if (err instanceof ApiError && err.notFound) {
          return noGuides(apiId!);
        }
        throw err;
      }),
    enabled: Boolean(apiId),
    staleTime: GUIDES_STALE_TIME,
  });
}

export function useGuide(apiId: string | undefined, guideId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.guide(apiId ?? '', guideId ?? ''),
    queryFn: () => getGuide(apiId!, guideId!),
    enabled: Boolean(apiId && guideId),
    staleTime: GUIDES_STALE_TIME,
  });
}
